import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function create() {
    setBusy(true);
    setErr("");
    try {
      await api.post("/admin/users", form);
      setAdding(false);
      setForm({ name: "", email: "", password: "", role: "editor" });
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  }

  return (
    <section data-testid="users-section">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-[#1F2547]">
            Team
          </h1>
          <p className="text-sm text-[#5B6079] font-medium mt-1">
            Admins have full control. Editors can manage Worksheets and Offers only.
          </p>
        </div>
        <button
          data-testid="user-add-btn"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1F2547] text-white font-bold rounded-full brutal-shadow-sm btn-press"
        >
          <Plus className="w-4 h-4" /> Add team member
        </button>
      </header>

      {adding && (
        <div data-testid="user-form" className="mb-6 rounded-2xl bg-white brutal-shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl text-[#1F2547]">New team member</h2>
            <button onClick={() => setAdding(false)} className="text-[#5B6079]">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                Name
              </label>
              <input
                data-testid="user-field-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                Email
              </label>
              <input
                data-testid="user-field-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                Password
              </label>
              <input
                data-testid="user-field-password"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                placeholder="Share with your team member"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                Role
              </label>
              <select
                data-testid="user-field-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium bg-white"
              >
                <option value="editor">Editor (worksheets + offers only)</option>
                <option value="admin">Admin (full control)</option>
              </select>
            </div>
          </div>

          {err && (
            <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-medium">
              {err}
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <button
              data-testid="user-save-btn"
              onClick={create}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow-sm btn-press disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Create
            </button>
            <button
              onClick={() => setAdding(false)}
              className="px-5 py-2.5 bg-[#EFE9F7] text-[#1F2547] font-bold rounded-full btn-press"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#5B6079] font-medium">Loading…</div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <div
              key={u.id}
              data-testid={`user-row-${u.id}`}
              className="bg-white rounded-2xl brutal-shadow-sm p-5 flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-full bg-[#9F87D8] text-white flex items-center justify-center font-bold">
                {(u.name || u.email)[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-bold text-base text-[#1F2547]">
                    {u.name || u.email}
                  </h3>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${
                      u.role === "admin"
                        ? "bg-[#1F2547] text-white"
                        : "bg-[#D4EDDB] text-[#1F2547]"
                    }`}
                  >
                    {u.role}
                  </span>
                </div>
                <div className="text-xs text-[#5B6079] font-medium">{u.email}</div>
              </div>
              <button
                data-testid={`user-delete-${u.id}`}
                onClick={() => remove(u.id)}
                className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
